import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { inject } from 'mobx-react'
import PropTypes from 'prop-types'
import get from 'lodash/get'

import { formatMinutesToHoursAndMinutes, formatDate } from '@atheneum/api/dist/lib'

import ACL from '@components/ACL'
import Checkbox from '@components/Checkbox'
import { showDialog, isCancellationError } from '@components/Dialog'
import NoContent from '@components/Error/NoContent'
import CheckIcon from '@components/Icon/Check'
import DashIcon from '@components/Icon/Dash'
import DeleteIcon from '@components/Icon/Delete'
import EditIcon from '@components/Icon/Edit'
import Tooltip from '@components/Tooltip'

class OutcomeTable extends Component {
  static propTypes = {
    deliverableStore: PropTypes.object,
    projectStore: PropTypes.object,
    feeStore: PropTypes.object,
    ruleStore: PropTypes.object,
    staticData: PropTypes.object,
    sidebarStore: PropTypes.object,
    growlStore: PropTypes.object
  }

  deleteFee = (fee, feeType, feeCost) => {
    const { deliverableStore, projectStore, feeStore, growlStore } = this.props
    let feeText = ''
    if (feeCost) {
      if (feeCost.entityName) feeText = `- ${feeCost.entityName}`
      if (feeCost.invoiceNumber) feeText = `- ${feeCost.invoiceNumber}`
    }
    if (fee.expert) feeText = `- ${fee.expert.title} ${fee.expert.fullName}`
    return showDialog({
      title: 'Delete this fee?',
      text: `${feeType} ${feeText}`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    })
      .then(() => {
        return feeStore.delete(fee.id).then(res => {
          deliverableStore.get(projectStore.id)
        })
      })
      .catch(err => {
        if (isCancellationError(err)) return
        growlStore.showError(err)
      })
  }

  calculateTotalDeliverables = () => {
    let totalAmountFee = 0
    let totalAmountCost = 0
    let totalAmountCredits = 0

    this.props.deliverableStore.deliverables.map(deliverable => {
      const totals = this.calculateDeliverablesTotals(deliverable)
      totalAmountFee += totals.totalAmountFee
      totalAmountCost += totals.totalAmountCost
      totalAmountCredits += totals.totalAmountCredits
      return deliverable
    })

    const data = {
      totalAmountCost,
      totalAmountFee,
      totalAmountCredits
    }

    return data
  }

  calculateDeliverablesTotals = deliverable => {
    let totalAmountFee = 0
    let totalAmountCost = 0
    let totalAmountCredits = 0

    const feesList = deliverable.fees
    feesList.map(fee => {
      let hasCost = 0
      let hasFee = 0
      let credits = 0

      fee.feeItems.map(feeItem => {
        if (feeItem.costTypeId) hasCost = feeItem.costTypeId
        if (feeItem.feeTypeId) hasFee = feeItem.feeTypeId
        if (feeItem.credits) credits = feeItem.credits
        return feeItem
      })

      if (hasFee) {
        fee.feeItems.map(item => {
          if (item.feeTypeId && item.converted) totalAmountFee += item.converted
          return item
        })
      }
      if (hasCost) {
        fee.feeItems.map(item => {
          if (item.costTypeId && item.converted) {
            totalAmountCost += item.converted
          }
          return item
        })
      }

      if (credits) totalAmountCredits += credits

      return fee
    })

    const data = {
      totalAmountCost,
      totalAmountFee,
      totalAmountCredits
    }
    return data
  }

  renderNestedTable = () => {
    const deliverablesList = this.props.deliverableStore.deliverables
    return deliverablesList.map(deliverable => {
      const totals = this.calculateDeliverablesTotals(deliverable)
      return (
        <tbody key={deliverable.id}>
          <tr className='title'>
            <th>{deliverable.deliverableType.name}</th>
            <th />
            <th />
            <th />
            <th />
            <th className='center'>
              {(totals.totalAmountFee - totals.totalAmountCost).toFixed(2)}
            </th>
            <th className='center'>
              {Number(totals.totalAmountCost).toFixed(2)}
            </th>
            <th className='center'>
              {Number(totals.totalAmountFee).toFixed(2)}
            </th>
            <th className='center'>{totals.totalAmountCredits}</th>
            <th />
            <th />
          </tr>
          {this.renderNestedTableRow(deliverable)}
        </tbody>
      )
    })
  }

  openContractSidebar = contractId => {
    this.props.sidebarStore.openSidebar({
      template: 'contract',
      id: contractId
    })
  }

  renderNestedTableRow = deliverable => {
    const { staticData } = this.props
    const feesList = deliverable.fees

    return feesList.map(fee => {
      const duration = formatMinutesToHoursAndMinutes(fee.consultationDuration)

      let hasCost = 0
      let hasFee = 0
      let feeFee = '-'
      let feeCost = '-'
      let credits

      fee.feeItems.map(feeItem => {
        if (feeItem.costTypeId) hasCost = feeItem.costTypeId
        if (feeItem.feeTypeId) hasFee = feeItem.feeTypeId
        if (feeItem.credits) credits = feeItem.credits
        return feeItem
      })

      let feeType
      if (hasFee) {
        feeType = staticData.getDataFromArray(hasFee, 'feeTypes')
      }
      if (hasCost && !hasFee) {
        feeType = staticData.getDataFromArray(hasCost, 'costTypes')
      }
      if (hasFee) {
        feeFee = fee.feeItems.find(item => {
          return item.feeTypeId ? item : null
        })
      }

      if (hasCost) {
        feeCost = fee.feeItems.find(item => {
          return item.costTypeId ? item : null
        })
      }

      const backdatingAllowed = this.props.ruleStore.isDateInAllowedBounds(
        fee.deliveryDate
      )

      const readableFeeCurrency = staticData.getDataFromArray(
        fee.currencyId,
        'currencies'
      )

      return (
        <tr key={fee.id} className=''>
          <td>
            {Boolean(hasFee) && (
              <Tooltip
                tooltip={
                  backdatingAllowed
                    ? 'Mark fee for batch action'
                    : 'Backdating rule in effect. No changes are available for this fee'
                }
              >
                <div className='outcome-table__checkbox-wrapper'>
                  <Checkbox
                    disabled={!backdatingAllowed || fee.invoiceId}
                    value={fee.checked}
                    onChange={val => (fee.checked = val)}
                  />
                </div>
              </Tooltip>
            )}
            <span className='text-wrapper'>
              <Link to={'fee/' + fee.id}>{feeType || '-'}</Link>
              {feeCost && feeCost.entityName ? (
                <span className='desc desc--top-spacing'>
                  {feeCost.entityName}
                </span>
              ) : null}
              {feeCost && feeCost.invoiceNumber ? (
                <span className='desc desc--top-spacing'>
                  {feeCost.invoiceNumber}
                </span>
              ) : null}
              {fee.expert ? (
                <span className='desc desc--top-spacing'>
                  {fee.expert.title} {fee.expert.fullName}
                </span>
              ) : null}
            </span>
          </td>
          <td>
            <span className='text-wrapper'>
              {get(fee, 'expertProjectLink.segment.name', '-')}
            </span>
          </td>
          <td>
            <span className='text-wrapper'>{formatDate(fee.deliveryDate)}</span>
          </td>
          <td>
            <span className='text-wrapper'>{duration || '-'}</span>
          </td>
          <td>
            <span className='text-wrapper'>{this.renderEmployees(fee)}</span>
          </td>
          <td className='center'>
            <span className='text-wrapper'>{this.renderMargins(fee)}</span>
          </td>
          <td className='center'>
            <span className='text-wrapper'>
              {feeCost.converted ? Number(feeCost.converted).toFixed(2) : '-'}
            </span>
          </td>
          <td className='center'>
            <span className='text-wrapper'>
              {readableFeeCurrency}{' '}
              {feeFee.converted ? Number(feeFee.converted).toFixed(2) : '-'}
            </span>
          </td>
          <td className='center'>
            <span className='text-wrapper'>{credits || '-'}</span>
          </td>
          <td
            className='center link'
            onClick={() =>
              this.openContractSidebar(get(fee, 'contract.id', ''))
            }
          >
            {get(fee, 'contract.contractReference', '')}
          </td>
          <td className='right'>
            {fee.invoiceId ? (
              <Tooltip tooltip={fee.invoiceNumber} disabled>
                <div>
                  <CheckIcon className='green-icon' />
                </div>
              </Tooltip>
            ) : (
                <DashIcon className='red-icon' />
              )}
          </td>
          <td className='no-wrap'>
            {this.renderFeeActions(fee, feeType, feeCost, hasFee, hasCost)}
          </td>
        </tr>
      )
    })
  }

  renderFeeActions = (fee, feeType, feeCost, hasFee, hasCost) => {
    const { ruleStore, projectStore } = this.props

    const isBackdating = ruleStore.isDeliveryDateAffectedByBackdating(
      fee.deliveryDate
    )

    if (isBackdating && (hasCost && !hasFee)) return

    let edit = (
      <span
        className='clickable'
        onClick={() =>
          this.props.feeStore.open(fee.id, projectStore.project.id)
        }
      >
        <EditIcon />
      </span>
    )

    const editAction = isBackdating ? (
      <ACL roles={['Accounting', 'Admin']} action>
        {edit}
      </ACL>
    ) : (
        edit
      )

    const deleteAvailable = ruleStore.isDateInAllowedBounds(fee.deliveryDate)

    const deleteAction = deleteAvailable && (
      <span
        className='right clickable'
        onClick={() => this.deleteFee(fee, feeType, feeCost)}
      >
        <DeleteIcon />
      </span>
    )

    return (
      <div>
        {editAction}
        {deleteAction}
      </div>
    )
  }

  renderEmployees = fee => {
    return <span>{fee.employee?.fullName}</span>
  }

  renderMargins = fee => {
    const feeValue = fee.feeItems.reduce(
      (t, fi) => (fi.feeTypeId ? t + fi.converted : t),
      0
    )
    // TODO: replace magic number 1 with CONST
    const costValue = fee.feeItems.reduce(
      (t, fi) => (fi.costTypeId === 1 ? t + fi.converted : t),
      0
    )
    return Number(feeValue - costValue).toFixed(2)
  }

  renderNoContent = () => {
    return <NoContent label='There are no fees and costs on this project yet' />
  }

  render() {
    const totals = this.calculateTotalDeliverables()
    const isEmpty = this.props.deliverableStore.deliverables.length < 1
    if (isEmpty) return this.renderNoContent()

    return (
      <table className='table-wrapper--nested'>
        <thead>
          <tr>
            <th>Fee type / Expert name</th>
            <th>Segment</th>
            <th>Delivery date</th>
            <th>Details</th>
            <th>Delivered employee</th>
            <th className='center'>Margin (EUR)</th>
            <th className='center'>Cost (EUR)</th>
            <th className='center'>Fee (EUR)</th>
            <th className='center'>Credits</th>
            <th className='center'>Contract</th>
            <th>Invoice</th>
            <th />
          </tr>
        </thead>
        {this.renderNestedTable()}
        <tfoot>
          <tr className='title'>
            <td>Total</td>
            <td />
            <td />
            <td />
            <td />
            <td className='center'>
              {(
                Number(totals.totalAmountFee) - Number(totals.totalAmountCost)
              ).toFixed(2)}
            </td>
            <td className='center'>
              {Number(totals.totalAmountCost).toFixed(2)}
            </td>
            <td className='center'>
              {Number(totals.totalAmountFee).toFixed(2)}
            </td>
            <td className='center'>
              {Number(totals.totalAmountCredits).toFixed(2)}
            </td>
            <td />
            <td />
          </tr>
        </tfoot>
      </table>
    )
  }
}

export default inject(
  'deliverableStore',
  'staticData',
  'feeStore',
  'ruleStore',
  'sidebarStore',
  'projectStore',
  'userStore',
  'growlStore'
)(OutcomeTable)
